import { Component, OnInit, Renderer2, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { AnalyticsType } from "./theme/admin/interfaces/analytics.interface";
import { PublicService } from "./project/client/modules/shared/services/public.service";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly publicService = inject(PublicService);
  private readonly platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.publicService.getAnalytics().subscribe({
      next: ({ data }: any) => {
        this.runWhenIdle(() => {
          data.forEach(({ type, token, active }: any) => {
            if (type && active) this.renderAnalytics(type, token);
          });
        });
      }
    });
  }
  private runWhenIdle(callback: () => void): void {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(callback, { timeout: 3000 });
    } else {
      setTimeout(callback, 2000);
    }
  }

  private renderAnalytics(type: AnalyticsType, key: string): void {
    switch (type) {
      case AnalyticsType.FacebookPixel:
        this.loadFacebookPixel(key);
        break;
      case AnalyticsType.GoogleAnalytics:
        this.loadGoogleAnalytics(key);
        break;
      case AnalyticsType.Clarity:
        this.loadClarity(key);
        break;
    }
  }

  private loadFacebookPixel(pixelId: string): void {
    if (this.document.getElementById('pixel-script')) return;

    const pixelCode = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');`;

    this.appendInlineScript('pixel-script', pixelCode);
  }

  private loadClarity(projectId: string): void {
    if (this.document.getElementById('clarity-script')) return;

    const clarityCode = `(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");`;

    this.appendInlineScript('clarity-script', clarityCode);
  }

  private loadGoogleAnalytics(trackingID: string): void {
    if (this.document.getElementById('ga-script')) return;

    const gaScript = this.renderer.createElement('script');
    this.renderer.setAttribute(gaScript, 'id', 'ga-script');
    this.renderer.setAttribute(gaScript, 'async', 'true');
    this.renderer.setAttribute(gaScript, 'defer', 'true');
    this.renderer.setAttribute(gaScript, 'src', `https://www.googletagmanager.com/gtag/js?id=${trackingID}`);
    this.renderer.appendChild(this.document.head, gaScript);

    const gaInitCode = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${trackingID}');`;

    this.appendInlineScript('ga-init-script', gaInitCode);
  }

  private appendInlineScript(id: string, textContent: string): void {
    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'id', id);
    this.renderer.setAttribute(script, 'type', 'text/javascript');
    this.renderer.setProperty(script, 'textContent', textContent);
    this.renderer.appendChild(this.document.head, script);
  }
}